/**
 * The element of the `/a/:accountPublicId` route (ADR-0302): everything inside
 * an account renders through here, and this is where a tab notices that its
 * URL account is not the session's.
 *
 * The session account comes from `api/sessionAccount`, not from
 * `activeAccount` alone: in a tab left behind by a switch elsewhere,
 * `activeAccount` still names the old account. The store learns the truth
 * from every auth response, from a 409 `account_mismatch` and from other tabs.
 *
 * - URL account = session account: the page, nothing else.
 * - A member account: the page stays mounted under a notice, and the
 *   transport is stopped while it shows.
 * - Not an account the user can see, or not an account id at all: a
 *   not-found page. It is the same answer for "does not exist" and "not
 *   yours", so it never reveals that another tenant exists.
 */

import { Fragment, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import { Outlet, useLocation, useNavigate as useRouterNavigate, useParams } from "react-router-dom";
import { Button, Center, Stack, Text, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";

import { clearSessionConflict, raiseSessionConflict } from "@/api/client";
import { getSessionAccount, getSessionState, subscribeSessionState } from "@/api/sessionAccount";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { AccountMismatchGate, type MismatchKind } from "@/routing/AccountMismatchGate";
import { sectionRoot } from "@/routing/sectionRoot";
import { stripTenantPrefix } from "@/routing/tenantPaths";

const ACCOUNT_PUBLIC_ID = /^org_[A-Za-z0-9_-]+$/;

function withRoot(account: string, root: string): string {
  return `/a/${account}${root === "/" ? "" : root}`;
}

function AccountNotFound({ home }: { home: string | null }) {
  const navigate = useRouterNavigate();
  return (
    <Center h="100%" p="xl">
      <Stack align="center" gap="sm" maw={420}>
        <Title order={3}>Page not found</Title>
        <Text size="sm" c="dimmed" ta="center">
          This page doesn't exist, or you don't have access to it.
        </Text>
        {home && (
          <Button variant="light" onClick={() => navigate(home, { replace: true })}>
            Go to your home page
          </Button>
        )}
      </Stack>
    </Center>
  );
}

export function TenantScope() {
  const { accountPublicId = "" } = useParams<{ accountPublicId: string }>();
  const { activeAccount, allAccounts, switchAccount, refreshUser } = useAuth();
  const { branding } = useBranding();
  const session = useSyncExternalStore(subscribeSessionState, getSessionState);
  const navigate = useRouterNavigate();
  const { pathname } = useLocation();
  const [busy, setBusy] = useState(false);
  // Accounts this tab has shown in agreement with the session: a later
  // mismatch there is "switched elsewhere", a first one is "arrived on a link".
  const agreedWith = useRef(new Set<string>());

  const isAccountId = ACCOUNT_PUBLIC_ID.test(accountPublicId);
  const sessionAccount = session.accountPublicId ?? activeAccount?.id ?? null;
  const agrees = isAccountId && session.notice === null && sessionAccount === accountPublicId;
  if (agrees) agreedWith.current.add(accountPublicId);

  // Equality with the active account first: a superadmin or support session
  // can be active in an account it is not a listed member of.
  const visible =
    accountPublicId === activeAccount?.id || allAccounts.some((a) => a.id === accountPublicId);
  const accountName =
    allAccounts.find((a) => a.id === accountPublicId)?.name ??
    (activeAccount?.id === accountPublicId ? activeAccount.name : null);
  const sessionAccountName =
    (sessionAccount && allAccounts.find((a) => a.id === sessionAccount)?.name) ??
    (activeAccount?.id === sessionAccount ? activeAccount?.name ?? null : null);

  // Stop or resume this tab's transport. A layout effect: before the browser
  // paints the notice, nothing more leaves the tab.
  useLayoutEffect(() => {
    if (!isAccountId) return;
    if (agrees) {
      clearSessionConflict();
    } else {
      raiseSessionConflict({ tabAccount: accountPublicId, sessionAccount });
      // Error toasts from requests that were cut off would contradict the notice.
      notifications.clean();
    }
  }, [agrees, isAccountId, accountPublicId, sessionAccount]);

  // Leaving the tenant subtree (browser back to /docs) must not leave the
  // transport stopped on a page that has no account.
  useEffect(() => () => clearSessionConflict(), []);

  // The tab strip names the account (Decision 6).
  useEffect(() => {
    const base = branding.productName || "Verity";
    document.title = accountName ? `${accountName} · ${base}` : base;
    return () => {
      document.title = base;
    };
  }, [accountName, branding.productName]);

  if (!isAccountId) {
    return <AccountNotFound home={sessionAccount ? withRoot(sessionAccount, "/") : null} />;
  }

  const kind: MismatchKind | null = agrees
    ? null
    : session.notice ?? (agreedWith.current.has(accountPublicId) ? "switched" : "arrival");

  if (kind !== null && !visible && session.notice === null) {
    return <AccountNotFound home={sessionAccount ? withRoot(sessionAccount, "/") : null} />;
  }

  const root = sectionRoot(stripTenantPrefix(pathname));

  const continueInSession = async () => {
    setBusy(true);
    try {
      // The session moved elsewhere; this tab's user state is stale.
      if (activeAccount?.id !== sessionAccount) await refreshUser();
      const target = getSessionAccount();
      if (target) navigate(withRoot(target, root), { replace: true });
    } finally {
      setBusy(false);
    }
  };

  const switchToTab = async () => {
    setBusy(true);
    try {
      // The URL already names this account: switch without navigating, so the
      // page underneath (and what was typed into it) stays.
      const ok = await switchAccount(accountPublicId, { navigate: false });
      if (!ok) {
        notifications.show({
          title: "Could not switch accounts",
          message: "Please try again.",
          color: "red",
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const signIn = () => {
    window.location.assign("/login");
  };

  // The page keeps ONE position in the tree whether or not the notice shows:
  // moving it would remount it and lose what the user typed. Keyed on the
  // account: a switch that lands on the same route pattern in another account
  // remounts the page instead of keeping the old account's state. A tab that
  // arrived on a link has no page worth keeping, so none is rendered there.
  const showPage = kind === null || kind === "switched" || kind === "signed-out" || kind === "other-user";
  return (
    <>
      {showPage && (
        <Fragment key={accountPublicId}>
          <Outlet />
        </Fragment>
      )}
      {kind !== null && (
        <AccountMismatchGate
          kind={kind}
          tabAccountName={accountName ?? "this account"}
          sessionAccountName={sessionAccountName}
          busy={busy}
          onUseSession={continueInSession}
          onUseTab={switchToTab}
          onSignIn={signIn}
        />
      )}
    </>
  );
}
